import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
	Card,
	Grid,
	MenuItem,
	Typography,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import { ValidatorForm, SelectValidator } from "react-material-ui-form-validator";
import { PageHeaderTabs } from "features/health-safety-forms";
import {
	Page,
	PageContent,
	FormActions,
	FormButton,
	FormSubmitButton,
	ScrollToTopOnMount,
	SELECT_MENU_PROPS,
	SelectInput,
	SearchDropdown,
} from "frame/components";
import { useDispatch, useSelector } from "react-redux";
import { useRouter, useTerminology } from "utils";
import { useCurrentRole } from "utils/current-user";
import { canManageOrganisationRecords } from "utils/roles";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { HOME_ROUTE } from "features/home/home-routes";
import { onListRequested as onSitesListRequested } from "features/sites";
import { onList as onStaffList } from "features/staff/actions";
import { onList as onEquipmentList } from "../actions";
import { EQUIPMENT_ROUTE, EQUIPMENT_USAGE_LIST_ROUTE } from "../actions/types";
import EquipmentUsageFields from "./equipment-usage-fields";
import { parseReading, supportsUsageRecording } from "../utils/equipment-usage-math";

const useStyles = makeStyles((theme) => ({
	actions: {
		margin: theme.spacing(3, 0, 8, 0),
	},
	error: {
		color: theme.palette.error.main,
		marginTop: theme.spacing(1),
	},
}));

const emptyForm = {
	organisationEquipmentId: "",
	staffId: "",
	usageDate: moment(),
	siteId: "",
	scheduleEntryId: "",
	unitsUsed: null,
	startReading: null,
	endReading: null,
	notes: "",
	updateCurrentReading: false,
};

const isActiveStaff = (staff) => {
	const status = staff?.status ? String(staff.status).toLowerCase() : "";
	return status === "active" || staff?.isActive === true;
};

const EquipmentUsageAddPage = ({ usageId: usageIdProp }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const role = useCurrentRole();
	const { history, location, match } = useRouter();
	const { siteLabel } = useTerminology();
	const equipmentList = useSelector(({ equipment }) => equipment?.data || []);
	const siteList = useSelector(({ sites }) => sites.pagination?.results || []);
	const staffList = useSelector(({ staff }) => staff.pagination?.results || []);
	const currentStaffId = useSelector(({ profile }) => profile?.staffId);
	const featureFlags = useSelector(({ featureFlags }) => featureFlags);
	const organisationId = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const tasksEnabled = featureFlags?.tasks === true;
	const usageRecordingEnabled =
		featureFlags?.equipment === true &&
		featureFlags?.equipmentUsageRecording === true;
	const isAdmin = canManageOrganisationRecords(role);
	const isEditMode = Boolean(usageIdProp || match?.params?.usageId);
	const usageId = usageIdProp || match?.params?.usageId;
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(isEditMode);
	const [error, setError] = useState("");
	const [tasks, setTasks] = useState([]);

	const initialEquipmentId = useMemo(() => {
		const params = new URLSearchParams(location.search || "");
		return params.get("equipmentId") || "";
	}, [location.search]);

	const returnTo =
		(location.state && location.state.from) ||
		(initialEquipmentId
			? `${EQUIPMENT_ROUTE}/${initialEquipmentId}`
			: EQUIPMENT_USAGE_LIST_ROUTE);

	const activeEquipment = (equipmentList || []).filter(
		(eq) => eq.active && supportsUsageRecording(eq.maintenanceType)
	);
	const selectedEquipment = activeEquipment.find(
		(eq) => eq.id === form.organisationEquipmentId
	);

	const staffOptions = useMemo(
		() =>
			(staffList || [])
				.filter(
					(staff) =>
						isActiveStaff(staff) || staff.id === (form.staffId || "")
				)
				.map(({ id, firstName, lastName }) => ({
					label: `${firstName} ${lastName}`,
					value: id,
				}))
				.sort((a, b) => (a.label > b.label ? 1 : -1)),
		[staffList, form.staffId]
	);

	useEffect(() => {
		dispatch(onEquipmentList());
		dispatch(onSitesListRequested());
		if (isAdmin) {
			dispatch(onStaffList());
		}
	}, [dispatch, isAdmin]);

	useEffect(() => {
		if (
			featureFlags &&
			!featureFlags.fetch?.loading &&
			!usageRecordingEnabled
		) {
			history.replace(EQUIPMENT_ROUTE);
		}
	}, [featureFlags, usageRecordingEnabled, history]);

	useEffect(() => {
		if (isEditMode && !isAdmin) {
			history.replace(EQUIPMENT_ROUTE);
		}
	}, [isEditMode, isAdmin, history]);

	useEffect(() => {
		if (!isEditMode || !usageId || !organisationId) {
			return;
		}
		let cancelled = false;
		setLoading(true);
		safetyWiseApi
			.get(`${organisationId}/equipment-usage/${usageId}`)
			.then((res) => {
				if (cancelled) return;
				const row = res.data || {};
				setForm({
					organisationEquipmentId: row.organisationEquipmentId || "",
					staffId: row.staffId || "",
					usageDate: row.usageDate ? moment(row.usageDate) : moment(),
					siteId: row.siteId || "",
					scheduleEntryId: row.scheduleEntryId || "",
					unitsUsed: row.unitsUsed ?? null,
					startReading: row.startReading ?? null,
					endReading: row.endReading ?? null,
					notes: row.notes || "",
					updateCurrentReading: false,
				});
			})
			.catch(() => {
				if (!cancelled) {
					setError("Unable to load equipment usage record.");
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isEditMode, usageId, organisationId]);

	useEffect(() => {
		if (isEditMode || form.organisationEquipmentId) {
			return;
		}
		setForm((prev) => {
			const equipmentId = prev.organisationEquipmentId || initialEquipmentId;
			const match = (equipmentList || []).find((eq) => eq.id === equipmentId);
			const eligible =
				match && supportsUsageRecording(match.maintenanceType) ? match : null;
			return {
				...emptyForm,
				organisationEquipmentId: eligible ? initialEquipmentId : "",
				staffId: isAdmin && currentStaffId ? currentStaffId : "",
				startReading:
					eligible && eligible.currentHoursKms != null
						? eligible.currentHoursKms
						: null,
			};
		});
		setError("");
	}, [initialEquipmentId, equipmentList, isEditMode, form.organisationEquipmentId, isAdmin, currentStaffId]);

	useEffect(() => {
		if (isEditMode || !isAdmin || form.staffId || !currentStaffId) {
			return;
		}
		setForm((prev) => ({ ...prev, staffId: currentStaffId }));
	}, [isEditMode, isAdmin, form.staffId, currentStaffId]);

	useEffect(() => {
		if (!tasksEnabled || !organisationId) {
			setTasks([]);
			return;
		}
		let cancelled = false;
		const from = moment().subtract(90, "days").utc().toISOString();
		const to = moment().add(30, "days").utc().toISOString();
		safetyWiseApi
			.get(`${organisationId}/schedule?fromUtc=${from}&toUtc=${to}&includeCancelled=false`)
			.then((res) => {
				if (!cancelled) {
					setTasks(Array.isArray(res.data) ? res.data : []);
				}
			})
			.catch(() => {
				if (!cancelled) setTasks([]);
			});
		return () => {
			cancelled = true;
		};
	}, [tasksEnabled, organisationId]);

	const onSelectEquipment = useCallback(
		(equipmentId) => {
			const match = activeEquipment.find((eq) => eq.id === equipmentId);
			setForm((prev) => ({
				...prev,
				organisationEquipmentId: equipmentId,
				startReading:
					match && match.currentHoursKms != null
						? match.currentHoursKms
						: null,
				endReading: null,
				unitsUsed: null,
				updateCurrentReading: false,
			}));
		},
		[activeEquipment]
	);

	const goBack = () => {
		history.push(returnTo);
	};

	const onSubmit = async () => {
		setError("");
		const units = parseReading(form.unitsUsed);
		if (!form.organisationEquipmentId) {
			setError("Select equipment.");
			return;
		}
		if (isAdmin && !form.staffId) {
			setError("Select the staff member who used this equipment.");
			return;
		}
		if (units === null || units <= 0) {
			setError("Enter units used greater than zero.");
			return;
		}
		const start = parseReading(form.startReading);
		const end = parseReading(form.endReading);
		if (start !== null && end !== null && end < start) {
			setError("End reading cannot be lower than start reading.");
			return;
		}
		setSaving(true);
		try {
			const payload = {
				organisationEquipmentId: form.organisationEquipmentId,
				usageDate: moment(form.usageDate).startOf("day").utc().toISOString(),
				siteId: form.siteId || null,
				scheduleEntryId: form.scheduleEntryId || null,
				startReading: start,
				endReading: end,
				unitsUsed: units,
				notes: form.notes || null,
				updateCurrentReading: !!form.updateCurrentReading,
			};
			if (isAdmin) {
				payload.staffId = form.staffId || null;
			}
			if (isEditMode) {
				await safetyWiseApi.post(
					`${organisationId}/equipment-usage/update/${usageId}`,
					payload
				);
			} else {
				await safetyWiseApi.post(`${organisationId}/equipment-usage/create`, {
					...payload,
					source: "Standalone",
				});
			}
			goBack();
		} catch (err) {
			setError(
				err?.response?.data?.message ||
					err?.response?.data?.error ||
					`Unable to ${isEditMode ? "update" : "save"} equipment usage.`
			);
		} finally {
			setSaving(false);
		}
	};

	const equipmentOptions = activeEquipment
		.map((eq) => ({
			label: eq.equipment,
			value: eq.id,
		}))
		.sort((a, b) => (a.label > b.label ? 1 : -1));

	const siteOptions = (siteList || [])
		.filter((site) => site.status && site.status.toLowerCase() === "active")
		.sort((a, b) => (a.siteName > b.siteName ? 1 : -1));

	const pageTitle = isEditMode ? "Edit Equip Use" : "+ Equip Use";
	const formTitle = isEditMode ? "Edit Equipment Usage" : "Record Equipment Usage";

	return (
		<Page title={pageTitle}>
			<ScrollToTopOnMount />
			<PageHeaderTabs
				title={pageTitle}
				listTabPath={EQUIPMENT_ROUTE}
				hideAddTabButton={true}
				hideListTabButton={true}
				backActionPath={returnTo}
			/>
			<PageContent padSides padBottom padTop>
				<Grid container spacing={3}>
					<Grid item lg={6} md={12} sm={12} xs={12}>
						<ValidatorForm autoComplete="off" noValidate onSubmit={onSubmit}>
							<Card>
								<PageContent padSides padBottom padTop>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Typography gutterBottom variant="h3">
												{formTitle}
											</Typography>
											{!loading && activeEquipment.length === 0 && (
												<Typography variant="body2" color="textSecondary">
													No hours or km equipment is available for usage
													recording. Month-based equipment uses scheduled
													service dates — set Next Service on the equipment
													record instead.
												</Typography>
											)}
										</Grid>
										<Grid item xs={12}>
											<SearchDropdown
												id="usage-equipment"
												label="Select equipment *"
												disabled={loading || saving || equipmentOptions.length === 0}
												defaultOptions={equipmentOptions}
												value={form.organisationEquipmentId || ""}
												onChange={onSelectEquipment}
												props={{
													name: "organisationEquipmentId",
													validators: ["required"],
													errorMessages: ["This field is required"],
												}}
											/>
										</Grid>
										{isAdmin && (
											<Grid item xs={12}>
												<SelectInput
													id="usage-staff"
													label="Staff *"
													options={staffOptions}
													value={form.staffId || ""}
													disabled={loading || saving}
													onChange={(staffId) =>
														setForm((prev) => ({
															...prev,
															staffId: staffId || "",
														}))
													}
												/>
											</Grid>
										)}
										<Grid item xs={12} sm={6}>
											<DatePicker
												fullWidth
												inputVariant="outlined"
												label="Date"
												format="Do MMMM YYYY"
												value={form.usageDate}
												disableFuture
												disabled={loading || saving}
												onChange={(value) =>
													setForm((prev) => ({ ...prev, usageDate: value }))
												}
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<SelectValidator
												fullWidth
												id="usage-site"
												name="siteId"
												select
												label={siteLabel}
												variant="outlined"
												value={form.siteId || ""}
												SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
												disabled={loading || saving}
												onChange={(e) =>
													setForm((prev) => ({ ...prev, siteId: e.target.value }))
												}
											>
												<MenuItem value="">
													<em>None</em>
												</MenuItem>
												{siteOptions.map((site) => (
													<MenuItem key={site.id} value={site.id}>
														{site.siteName}
													</MenuItem>
												))}
											</SelectValidator>
										</Grid>
										{tasksEnabled && (
											<Grid item xs={12}>
												<SelectValidator
													fullWidth
													id="usage-task"
													name="scheduleEntryId"
													select
													label="Task"
													variant="outlined"
													value={form.scheduleEntryId || ""}
													SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
													disabled={loading || saving}
													onChange={(e) =>
														setForm((prev) => ({
															...prev,
															scheduleEntryId: e.target.value,
														}))
													}
												>
													<MenuItem value="">
														<em>None</em>
													</MenuItem>
													{tasks.map((task) => (
														<MenuItem key={task.id} value={task.id}>
															{task.title}
														</MenuItem>
													))}
												</SelectValidator>
											</Grid>
										)}
										<Grid item xs={12}>
											<EquipmentUsageFields
												unitType={selectedEquipment?.maintenanceType}
												unitsUsed={form.unitsUsed}
												startReading={form.startReading}
												endReading={form.endReading}
												notes={form.notes}
												updateCurrentReading={form.updateCurrentReading}
												disabled={loading || saving}
												onChange={(next) =>
													setForm((prev) => ({
														...prev,
														...next,
													}))
												}
											/>
										</Grid>
									</Grid>
									{error && (
										<Typography className={classes.error} variant="body2">
											{error}
										</Typography>
									)}
								</PageContent>
							</Card>
							<FormActions className={classes.actions}>
								<FormSubmitButton disabled={saving || loading} loading={saving}>
									Save
								</FormSubmitButton>
								<FormButton onClick={goBack} disabled={saving}>
									Cancel
								</FormButton>
							</FormActions>
						</ValidatorForm>
					</Grid>
				</Grid>
			</PageContent>
		</Page>
	);
};

export default EquipmentUsageAddPage;
